
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Profile } from '@/types/auth';
import { useToast } from '@/hooks/use-toast';
import { refreshAndValidateSession } from '@/utils/auth-cleanup';

export const useProfile = (identifier?: string) => {
  const { toast } = useToast();

  const { data: profile, isLoading, error, refetch } = useQuery({
    queryKey: ['profile', identifier],
    queryFn: async () => {
      if (!identifier) return null;

      try {
        console.log('useProfile queryFn - Fetching profile for:', identifier);
        
        // Always try to fetch by username first
        const { data: profileData, error: usernameError } = await supabase
          .from('public_profiles')
          .select('*')
          .eq('username', identifier)
          .maybeSingle();
          
        if (profileData) {
          console.log('Found profile by username:', profileData.username, 'Data:', profileData);
          
          // Get follower and following counts
          const { count: followersCount } = await supabase
            .from('followers')
            .select('*', { count: 'exact', head: true })
            .eq('following_id', profileData.id);
            
          const { count: followingCount } = await supabase
            .from('followers')
            .select('*', { count: 'exact', head: true })
            .eq('follower_id', profileData.id);
            
          return {
            ...profileData,
            followers_count: followersCount ?? 0,
            following_count: followingCount ?? 0,
          } as Profile;
        }

        // If no profile found by username, try by UUID if it's a valid UUID
        if (!profileData && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(identifier)) {
          console.log('Trying to fetch profile by UUID:', identifier);
          
          const { data: uuidData, error: uuidError } = await supabase
            .from('public_profiles')
            .select('*')
            .eq('id', identifier)
            .maybeSingle();
            
          if (uuidError) throw uuidError;
          
          if (uuidData) {
            console.log('Found profile by UUID:', uuidData.id, 'Data:', uuidData);
            // Get follower and following counts
            const { count: followersCount } = await supabase
              .from('followers')
              .select('*', { count: 'exact', head: true })
              .eq('following_id', uuidData.id);
              
            const { count: followingCount } = await supabase
              .from('followers')
              .select('*', { count: 'exact', head: true })
              .eq('follower_id', uuidData.id);
              
            return {
              ...uuidData,
              followers_count: followersCount ?? 0,
              following_count: followingCount ?? 0,
            } as Profile;
          }
        }
        
        console.log('No profile found for identifier:', identifier);
        throw new Error('Profile not found');
      } catch (error) {
        console.error('Error in profile fetch:', error);
        toast({
          title: "Error fetching profile",
          description: error instanceof Error ? error.message : "Could not load profile",
          variant: "destructive",
        });
        throw error;
      }
    },
    staleTime: 1000 * 60 * 5, // Consider data fresh for 5 minutes
    gcTime: 1000 * 60 * 30, // Keep in cache for 30 minutes
    retry: 2, // Retry failed requests 2 times
  });

  return {
    profile,
    isLoading,
    error,
    refetch,
  };
};
